import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import {
  consultarMarca,
  fetchClassesNice,
  fetchMarcasAnalise,
  fetchStatusLote,
  iniciarConsultaLote,
  MarcaApiError,
  sugerirClassesProduto,
} from './marcas/marcasService'
import {
  formatarDataHora,
  leituraTexto,
  linhasExportProcessos,
  linhasExportResumo,
  linkInpi,
  NIVEL_EMOJI,
  NIVEL_LABEL,
  nomeArquivoLote,
  parsearListaMarcas,
  produtoTexto,
  rotuloClasse,
  selecaoDaSugestao,
} from './marcas/logic'
import { paraCsv } from './recompra/logic'
import type {
  ClasseNice,
  ClasseSelecionada,
  ProcessoMarca,
  ProdutoAnalise,
  ResultadoIndividual,
  ResultadoLoteItem,
  StatusLote,
} from './marcas/types'

const POLL_MS = 4000

const COR_NIVEL: Record<string, string> = {
  alto: '#b91c1c',
  medio: '#c2410c',
  baixo: '#15803d',
  erro: '#475569',
}

function baixar(nome: string, texto: string, mime: string) {
  const url = URL.createObjectURL(new Blob([texto], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function TabelaProcessos({ processos }: { processos: ProcessoMarca[] }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Número</TableCell>
            <TableCell>Marca</TableCell>
            <TableCell>Titular</TableCell>
            <TableCell>Situação</TableCell>
            <TableCell>Classes</TableCell>
            <TableCell>Link</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processos.map((p) => {
            const link = linkInpi(p.codPedido)
            return (
              <TableRow key={p.numero} hover>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{p.numero}</TableCell>
                <TableCell>{p.marca}</TableCell>
                <TableCell>{p.titular}</TableCell>
                <TableCell>{p.situacao}</TableCell>
                <TableCell>
                  {p.classes.length
                    ? p.classes.map((c) => {
                        const rotulo = rotuloClasse(c)
                        return <Chip key={rotulo} label={rotulo} size="small" sx={{ mr: 0.5 }} />
                      })
                    : 'Classe não informada'}
                </TableCell>
                <TableCell>
                  {link ? (
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      detalhes
                    </a>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

function ResultadoBusca({ resultado }: { resultado: ResultadoIndividual }) {
  const { risco } = resultado
  const [mostrarInativos, setMostrarInativos] = useState(false)
  const cor = COR_NIVEL[risco.nivel]

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Processos ativos
            </Typography>
            <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', mt: 0.5, color: cor }}>
              {risco.ativos.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Inativos/extintos
            </Typography>
            <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', mt: 0.5 }}>
              {risco.inativos.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Classes em conflito
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, mt: 0.5 }}>
              {risco.categorias_risco?.length ? risco.categorias_risco.join(', ') : '—'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {risco.parcial ? (
        <Alert severity="warning">
          {risco.parcial.aviso}
          {risco.parcial.motivo ? ` Motivo: ${risco.parcial.motivo}.` : ''}
        </Alert>
      ) : null}

      <Card sx={{ overflow: 'hidden' }}>
        <Box sx={{ bgcolor: cor, color: '#fff', px: 2.25, py: 1.25, display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: 14 }}>
            {risco.emoji} Risco {NIVEL_LABEL[risco.nivel]}
          </Typography>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 999, px: 1.1, fontSize: 12, fontWeight: 600 }}>
            {risco.ativos.length} ativos
          </Box>
          {risco.alto_renome ? (
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 999, px: 1.1, fontSize: 12, fontWeight: 600 }}>
              Alto renome
            </Box>
          ) : null}
        </Box>
        <Box sx={{ px: 2.25, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {risco.mensagem}
          </Typography>
          {risco.classes_produto?.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Analisado para o produto nas classes {produtoTexto('', risco.classes_produto)}.
            </Typography>
          ) : null}
          {risco.observacao ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Obs: {risco.observacao}
            </Typography>
          ) : null}
        </Box>
      </Card>

      {risco.ativos.length ? (
        <Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: 13.5, mb: 1 }}>
            Processos ativos ({risco.ativos.length})
          </Typography>
          <TabelaProcessos processos={risco.ativos} />
        </Box>
      ) : null}

      {risco.inativos.length ? (
        <Box>
          <Button size="small" onClick={() => setMostrarInativos((v) => !v)}>
            {mostrarInativos ? 'Ocultar' : 'Mostrar'} processos inativos/extintos ({risco.inativos.length})
          </Button>
          {mostrarInativos ? (
            <Box sx={{ mt: 1 }}>
              <TabelaProcessos processos={risco.inativos} />
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Stack>
  )
}

function LinhaLote({ item }: { item: ResultadoLoteItem }) {
  const { linha, risco } = item
  const cor = COR_NIVEL[linha.nivel] ?? COR_NIVEL.erro

  return (
    <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid rgba(15,23,42,0.08)' }}>
      <AccordionSummary expandIcon={<span style={{ fontSize: 12, color: '#64748b' }}>▶</span>}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ width: '100%' }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, minWidth: 140 }}>
            {linha.marca}
          </Typography>
          <Chip
            size="small"
            label={`${linha.nivel === 'erro' ? '⚠️' : NIVEL_EMOJI[linha.nivel as 'alto' | 'medio' | 'baixo']} ${linha.nivel.toUpperCase()}`}
            sx={{ bgcolor: `${cor}1a`, color: cor, fontWeight: 700 }}
          />
          {risco?.alto_renome ? <Chip size="small" color="error" variant="outlined" label="Alto renome" /> : null}
          {risco?.parcial ? (
            <Chip size="small" color="warning" variant="outlined" label={leituraTexto(risco)} />
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {linha.nivel === 'erro' ? linha.erro_msg : `${linha.qtd_ativos} ativos`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 1 }}>
            {linha.classes_conflito.join(', ') || (linha.nivel === 'erro' ? '' : '—')}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {risco ? (
          <Stack spacing={1.5}>
            {risco.parcial ? (
              <Alert severity="warning">
                {risco.parcial.aviso}
                {risco.parcial.motivo ? ` Motivo: ${risco.parcial.motivo}.` : ''}
              </Alert>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {risco.mensagem}
              {linha.titulares.length ? ` Titular(es): ${linha.titulares.join(', ')}.` : ''}
            </Typography>
            {risco.ativos.length ? <TabelaProcessos processos={risco.ativos} /> : null}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {linha.erro_msg}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

const PAPEL_LABEL: Record<ClasseSelecionada['papel'], string> = {
  principal: 'principal',
  vizinha: 'vizinha',
  manual: 'escolhida',
}

/** Produto que se quer vender + classes de Nice dele. Opcional: em branco, a análise usa a
 * regra geral. A IA só SUGERE as classes — quem decide é a seleção de selos, editável. */
function CampoProduto({
  produto,
  onProduto,
  classes,
  onClasses,
  catalogo,
}: {
  produto: string
  onProduto: (v: string) => void
  classes: ClasseSelecionada[]
  onClasses: (v: ClasseSelecionada[]) => void
  catalogo: ClasseNice[]
}) {
  const [identificando, setIdentificando] = useState(false)
  const [aviso, setAviso] = useState<{ tipo: 'info' | 'warning'; texto: string } | null>(null)
  const titulo = (n: number) => catalogo.find((c) => c.numero === n)?.titulo ?? ''
  const disponiveis = catalogo.filter((c) => !classes.some((s) => s.numero === c.numero))

  const identificar = async () => {
    const valor = produto.trim()
    if (!valor) return
    setIdentificando(true)
    setAviso(null)
    try {
      const s = await sugerirClassesProduto(valor)
      if (s.ok) {
        onClasses(selecaoDaSugestao(s))
        if (s.explicacao) setAviso({ tipo: 'info', texto: s.explicacao })
        else if (s.fonte === 'cache') setAviso({ tipo: 'info', texto: 'Classes sugeridas pela IA numa consulta anterior.' })
      } else {
        setAviso({ tipo: 'warning', texto: s.erro })
      }
    } catch (e) {
      setAviso({
        tipo: 'warning',
        texto: `${e instanceof Error ? e.message : 'Falha ao identificar as classes.'} Escolha as classes na lista.`,
      })
    } finally {
      setIdentificando(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="flex-end" flexWrap="wrap" useFlexGap>
            <TextField
              label="Produto que você quer vender (opcional)"
              placeholder="ex.: árvore de natal"
              size="small"
              value={produto}
              onChange={(e) => {
                onProduto(e.target.value)
                // as classes eram do produto anterior — não deixa valer pro texto novo
                if (classes.length) onClasses([])
                setAviso(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') identificar()
              }}
              sx={{ flex: '1 1 280px' }}
            />
            <Button
              variant="outlined"
              disabled={identificando || !produto.trim()}
              startIcon={identificando ? <CircularProgress size={14} color="inherit" /> : undefined}
              onClick={identificar}
            >
              {identificando ? 'Identificando…' : 'Identificar classes'}
            </Button>
            <TextField
              select
              label="Adicionar classe"
              size="small"
              value=""
              disabled={!disponiveis.length}
              onChange={(e) => {
                const numero = Number(e.target.value)
                if (numero) onClasses([...classes, { numero, papel: 'manual' }])
              }}
              sx={{ flex: '0 1 220px' }}
            >
              {disponiveis.map((c) => (
                <MenuItem key={c.numero} value={c.numero} sx={{ whiteSpace: 'normal', maxWidth: 520 }}>
                  NCL {c.numero} — {c.titulo}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {classes.length ? (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                Classes consideradas:
              </Typography>
              {classes.map((c) => (
                <Chip
                  key={c.numero}
                  size="small"
                  color={c.papel === 'principal' ? 'primary' : 'default'}
                  variant={c.papel === 'principal' ? 'filled' : 'outlined'}
                  label={`NCL ${c.numero} · ${PAPEL_LABEL[c.papel]}`}
                  title={titulo(c.numero)}
                  onDelete={() => onClasses(classes.filter((x) => x.numero !== c.numero))}
                />
              ))}
            </Stack>
          ) : null}

          {aviso ? <Alert severity={aviso.tipo}>{aviso.texto}</Alert> : null}

          <Typography variant="caption" color="text.secondary">
            {classes.length
              ? 'Risco alto só para registro ativo nessas classes; registro em outro segmento vira risco médio. Marca de alto renome continua risco alto.'
              : produto.trim()
                ? 'Clique em "Identificar classes" (ou adicione à mão) para o produto valer na análise. Sem classes, vale a regra geral.'
                : 'Em branco, vale a regra geral: qualquer registro ativo em classe de produto é risco alto.'}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}

export function MarcasPage() {
  const [aba, setAba] = useState<'individual' | 'lote'>('individual')

  // ---- produto (vale pras duas abas) ----
  const [produto, setProduto] = useState('')
  const [classesProduto, setClassesProduto] = useState<ClasseSelecionada[]>([])
  const [catalogo, setCatalogo] = useState<ClasseNice[]>([])
  const produtoAnalise: ProdutoAnalise = {
    produto: produto.trim(),
    classes: classesProduto.map((c) => c.numero),
    principais: classesProduto.filter((c) => c.papel === 'principal').map((c) => c.numero),
  }

  useEffect(() => {
    fetchClassesNice()
      .then((r) => setCatalogo(r.classes))
      .catch(() => undefined)
  }, [])

  // ---- busca individual ----
  const [termo, setTermo] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [erroIndividual, setErroIndividual] = useState<string | null>(null)
  const [resultado, setResultado] = useState<ResultadoIndividual | null>(null)

  const consultar = async () => {
    const valor = termo.trim()
    if (!valor) return
    setBuscando(true)
    setErroIndividual(null)
    setResultado(null)
    try {
      const r = await consultarMarca(valor, produtoAnalise)
      setResultado(r)
    } catch (e) {
      setErroIndividual(e instanceof MarcaApiError || e instanceof Error ? e.message : 'Falha ao consultar o INPI.')
    } finally {
      setBuscando(false)
    }
  }

  // ---- lote ----
  const [textoLote, setTextoLote] = useState('')
  const [statusLote, setStatusLote] = useState<StatusLote | null>(null)
  const [erroLote, setErroLote] = useState<string | null>(null)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const carregarLeitura = useCallback(async () => {
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const data = await fetchMarcasAnalise()
    if (!data.ok) {
      setLeituraIa({
        loading: false,
        erro: data.erro || data.message || data.error || 'Leitura IA indisponível.',
      })
      return
    }
    setLeituraIa({
      loading: false,
      texto: data.texto || data.leitura,
      modelo: data.modelo,
      fonte: data.fonte,
      cached: data.cached,
      gerado_em: data.gerado_em,
    })
  }, [])

  const pararPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const iniciarPoll = useCallback(() => {
    pararPoll()
    pollRef.current = setInterval(async () => {
      const st = await fetchStatusLote().catch(() => null)
      if (!st) return
      setStatusLote(st)
      if (!st.rodando) {
        pararPoll()
        if (st.resultados?.length) carregarLeitura()
      }
    }, POLL_MS)
  }, [pararPoll, carregarLeitura])

  useEffect(() => {
    fetchStatusLote()
      .then((st) => {
        setStatusLote(st)
        if (st.rodando) iniciarPoll()
        else if (st.resultados?.length) carregarLeitura()
      })
      .catch(() => undefined)
    return pararPoll
  }, [iniciarPoll, pararPoll, carregarLeitura])

  const marcasDetectadas = parsearListaMarcas(textoLote)

  const rodarLote = async () => {
    if (!marcasDetectadas.length) return
    setErroLote(null)
    try {
      await iniciarConsultaLote(marcasDetectadas, produtoAnalise)
      const st = await fetchStatusLote()
      setStatusLote(st)
      iniciarPoll()
    } catch (e) {
      setErroLote(e instanceof MarcaApiError || e instanceof Error ? e.message : 'Falha ao disparar a consulta em lote.')
    }
  }

  const exportarLote = (tipo: 'resumo' | 'processos') => {
    const resultados = statusLote?.resultados ?? []
    if (!resultados.length) return
    const analisado = produtoTexto(statusLote?.produto, statusLote?.classes_produto)
    const aoa =
      tipo === 'resumo' ? linhasExportResumo(resultados, analisado) : linhasExportProcessos(resultados, analisado)
    baixar(nomeArquivoLote(tipo), paraCsv(aoa), 'text/csv;charset=utf-8')
  }

  return (
    <DomainPageShell
      title="Consulta Marcas INPI"
      subtitle="Consulta o registro de marcas no INPI e avalia o risco de takedown no Mercado Livre via BPP (Brand Protection Program)."
      loading={false}
      error={null}
    >
      <Stack spacing={2.5}>
        <Card>
          <CardContent sx={{ pb: '16px !important' }}>
            <Tabs value={aba} onChange={(_, v) => setAba(v)} sx={{ minHeight: 36 }}>
              <Tab value="individual" label="Buscar uma marca" sx={{ minHeight: 36, py: 0.5 }} />
              <Tab value="lote" label="Lista de marcas" sx={{ minHeight: 36, py: 0.5 }} />
            </Tabs>
          </CardContent>
        </Card>

        <CampoProduto
          produto={produto}
          onProduto={setProduto}
          classes={classesProduto}
          onClasses={setClassesProduto}
          catalogo={catalogo}
        />

        {aba === 'individual' ? (
          <Stack spacing={2.5}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="flex-end" flexWrap="wrap">
                  <TextField
                    label="Nome da marca"
                    size="small"
                    value={termo}
                    onChange={(e) => setTermo(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') consultar()
                    }}
                    sx={{ flex: '1 1 280px' }}
                  />
                  <Button
                    variant="contained"
                    disabled={buscando || !termo.trim()}
                    startIcon={buscando ? <CircularProgress size={14} color="inherit" /> : undefined}
                    onClick={consultar}
                  >
                    {buscando ? 'Consultando…' : 'Consultar no INPI'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {erroIndividual ? <Alert severity="error">{erroIndividual}</Alert> : null}
            {resultado ? <ResultadoBusca resultado={resultado} /> : null}
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <TextField
                    label="Marcas (uma por linha)"
                    size="small"
                    multiline
                    minRows={5}
                    value={textoLote}
                    onChange={(e) => setTextoLote(e.target.value)}
                    sx={{ flex: '1 1 320px' }}
                  />
                  <Stack spacing={1.25} sx={{ flex: '0 0 220px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {marcasDetectadas.length} marca(s) detectada(s).
                    </Typography>
                    <Button
                      variant="contained"
                      disabled={Boolean(statusLote?.rodando) || !marcasDetectadas.length}
                      startIcon={statusLote?.rodando ? <CircularProgress size={14} color="inherit" /> : undefined}
                      onClick={rodarLote}
                    >
                      {statusLote?.rodando ? 'Consultando…' : 'Rodar consulta em lote'}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {erroLote ? <Alert severity="error">{erroLote}</Alert> : null}
            {statusLote?.erro ? <Alert severity="warning">{statusLote.erro}</Alert> : null}

            {statusLote?.rodando ? (
              <Alert severity="info">
                Consultando {statusLote.processadas}/{statusLote.total} marcas — todas as páginas de cada marca
                são lidas, com pausa proposital entre os pedidos pro INPI não bloquear. Marca com muitos
                processos leva mais tempo.
              </Alert>
            ) : null}

            {statusLote && statusLote.resultados.length ? (
              <>
                <PropostaIaCard
                  title="Leitura do lote INPI"
                  caption="Reflecte o risco e os titulares deste lote e destaca o que a tabela sozinha não mostra. A verdade continua nos resultados abaixo — revise antes de agir."
                  state={leituraIa}
                  onRetry={carregarLeitura}
                />
                <Card sx={{ overflow: 'hidden' }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ px: 2.25, py: 1.5 }}
                >
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: 13.5, mr: 'auto' }}>
                    Resultado — ordenado por risco ({statusLote.resultados.length} marcas
                    {statusLote.concluido_em ? ` · verificado às ${formatarDataHora(statusLote.concluido_em)}` : ''})
                    {statusLote.classes_produto?.length ? (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        Produto analisado: {produtoTexto(statusLote.produto, statusLote.classes_produto)}
                      </Typography>
                    ) : null}
                  </Typography>
                  <Button size="small" variant="outlined" disabled={statusLote.rodando} onClick={() => exportarLote('resumo')}>
                    Exportar resumo
                  </Button>
                  <Button size="small" variant="outlined" disabled={statusLote.rodando} onClick={() => exportarLote('processos')}>
                    Exportar processos
                  </Button>
                </Stack>
                {statusLote.resultados.map((item) => (
                  <LinhaLote key={item.termo} item={item} />
                ))}
              </Card>
              </>
            ) : null}
          </Stack>
        )}
      </Stack>
    </DomainPageShell>
  )
}
